import { routePrefix, routes } from '@/utils/routes/routes';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Spinner from './Components/spinner';
import { getData } from '@/services/api.service';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { useDispatch, useSelector } from 'react-redux';
import store, { RootState } from '@/core/redux/store';
import NoProducts from './Components/noProduct';
import 'swiper/css';
import 'swiper/css/navigation';
import { FaShoppingBag, FaInfoCircle, FaStar, FaStarHalfAlt } from 'react-icons/fa';
import { TbEye } from 'react-icons/tb';
import { FiMinus, FiPlus } from 'react-icons/fi';
import { Modal, Button } from 'react-bootstrap';
import ProductView from './Components/productView';
import { addCartItem, clearCart, increaseQuantity, decreaseQuantity, updateQuantity } from '@/core/redux/cartSlice';
import { toast } from 'react-toastify';
import { trackProductSelect } from '@/utils/analytics';

interface Product {
  id: string;
  name: string;
  image_path: string;
  variant?: string;
  price: string;
  quantity: string;
  max_qty?: number;
  process_time?: number;
  express_wash_amount?: number;
  overall_rating?: number;
  review_count?: string;
}

interface Category {
  name: string;
  image_path: string;
  product: Product[];
}

const WareHouseProduct: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const user = useSelector((state: RootState) => state.common.userDetails);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currencyCode = useSelector(
    (state: RootState) => state.common.header?.currency?.symbol
  );
  const expressWashOnline = useSelector(
    (state: RootState) => state.common.header?.express_wash_online
  );
  const cartItems = useSelector((state: RootState) => state.cart?.cartItems || []);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [productList, setProductList] = useState<Category[]>([]);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [processTimeArray, setProcessTimeArray] = useState<number[]>([]);
  const [expressWashSelections, setExpressWashSelections] = useState<Record<string, boolean>>({});
  const [selectedProduct, setSelectedProduct] = useState<any>({});
  const [showProductModal, setShowProductModal] = useState<boolean>(false);
  const [hoveredProduct, setHoveredProduct] = useState<string | null>(null);

  const getShopProductList = async () => {
    setLoading(true);
    try {
      const response = await getData(
        `get_all_products?warehouse_id=${id}&type=service`
      );

      if (response.status === 200 || response.status === 201) {
        if (response?.data?.data?.length > 0) {
          setProductList(response.data.data);
          setActiveCategory(response.data.data[0].name);

          // Collect process_time values into an array
          const processTimes: number[] = [];
          response.data.data.forEach((category: any) => {
            category.product.forEach((product: any) => {
              // Collect process_time if it exists
              if (product.process_time !== undefined && product.process_time !== null) {
                processTimes.push(Number(product.process_time));
              }
            });
          });
          setProcessTimeArray(processTimes);
        }
      } else {
        throw new Error('Failed to fetch products');
      }
    } catch (error: any) {
      console.error('Error fetching products:', error);
      if (error?.code !== 'ERR_CANCELED') {
        toast.error('Failed to load products. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleIncrement = (productId: string) => {
    const isExpress = expressWashSelections[productId] || false;
    const itemId = isExpress ? `${productId}_express` : `${productId}_regular`;
    const inCart = cartItems?.find(item => item.id === itemId);

    setQuantities((prev) => {
      const currentQty = prev[productId] || 1;
      const newQty = currentQty + 1;
      if (inCart) {
        dispatch(increaseQuantity(itemId));
      }
      return { ...prev, [productId]: newQty };
    });
  };

  const handleDecrement = (productId: string) => {
    const isExpress = expressWashSelections[productId] || false;
    const itemId = isExpress ? `${productId}_express` : `${productId}_regular`;
    const inCart = cartItems?.find(item => item.id === itemId);

    setQuantities((prev) => {
      const currentQty = prev[productId] || 1;
      if (currentQty > 1) {
        const newQty = currentQty - 1;
        if (inCart) {
          dispatch(decreaseQuantity(itemId));
        }
        return { ...prev, [productId]: newQty };
      }
      return prev;
    });
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement>,
    productId: string
  ) => {
    const value = parseInt(e.target.value);
    const isExpress = expressWashSelections[productId] || false;
    const itemId = isExpress ? `${productId}_express` : `${productId}_regular`;
    const inCart = cartItems?.find(item => item.id === itemId);

    if (!isNaN(value) && value >= 1) {
      setQuantities((prev) => ({ ...prev, [productId]: value }));
      if (inCart) {
        dispatch(updateQuantity({ id: itemId, quantity: value }));
      }
    }
  };

  const handleExpressWashToggle = (productId: string, checked: boolean) => {
    setExpressWashSelections(prev => ({
      ...prev,
      [productId]: checked
    }));
  };

  const addToCart = (product: Product) => {
    const quantityToAdd = quantities[product.id] || 1;
    const expressWashSelected = expressWashSelections[product.id] || false;

    // Find the full product object from productList to get category_id, service_mode, and type
    let categoryId = '';
    let serviceMode = '';
    let productType = '';
    let expressWashAmount = 0;

    for (const category of productList) {
      const fullProduct = category.product.find((p) => p.id === product.id);
      if (fullProduct) {
        categoryId = (fullProduct as any).category_id || '';
        serviceMode = (fullProduct as any).service_mode || '';
        productType = (fullProduct as any).type || '';
        expressWashAmount = (fullProduct as any).express_wash_amount || 0;
        break;
      }
    }

    // Create unique IDs for express wash and regular wash items
    const itemId = expressWashSelected ? `${product.id}_express` : `${product.id}_regular`;
    const itemName = expressWashSelected ? `${product.name} (Express Wash)` : product.name;

    // Ensure proper number parsing
    const basePrice = parseFloat(product.price.toString()) || 0;
    const expressAmount = parseFloat(expressWashAmount?.toString()) || 0;
    // For express wash items, only charge express wash amount, not base price + express wash
    const itemPrice = expressWashSelected ? expressAmount : basePrice;

    const cartItem = {
      user_id: user?.id,
      id: itemId, // Use unique ID for express/regular variants
      name: itemName,
      price: itemPrice,
      image: product.image_path,
      category_id: categoryId,
      quantity: quantityToAdd,
      variant: expressWashSelected ? 'express' : 'regular',
      service_mode: serviceMode,
      process_time: product.process_time || 0,
      type: productType,
      express_wash_selected: expressWashSelected,
      express_wash_amount: expressWashSelected ? expressWashAmount : 0,
    };

    dispatch(addCartItem(cartItem));
    const messageType = expressWashSelected ? 'Express wash product' : 'Product';
    toast.success(`${messageType} added to cart successfully!`);
  };

  const handleCategoryClick = (categoryName: string) => {
    setActiveCategory(categoryName);
  };

  useEffect(() => {
    if (id) {
      getShopProductList();
    }
  }, [id]);

  // Sync internal quantities with cart items
  useEffect(() => {
    const newQuantities: Record<string, number> = {};
    let changed = false;

    // Initialize all existing products to 1
    productList.forEach(category => {
      category.product.forEach(product => {
        newQuantities[product.id] = 1;
      });
    });

    // Overwrite with cart quantities for matching variants
    if (cartItems && cartItems.length > 0) {
      cartItems.forEach(item => {
        const productId = item.id.split('_')[0];
        const isExpressItem = item.id.endsWith('_express');
        const currentSelectionIsExpress = expressWashSelections[productId] || false;

        if (isExpressItem === currentSelectionIsExpress) {
          if (newQuantities[productId] !== undefined) {
            newQuantities[productId] = item.quantity;
          }
        }
      });
    }

    // Check if we actually need to update the state to avoid unnecessary renders
    const isDifferent = Object.keys(newQuantities).some(
      key => newQuantities[key] !== quantities[key]
    );

    if (isDifferent) {
      setQuantities(newQuantities);
    }
  }, [cartItems, expressWashSelections, productList]);

  if (loading) {
    return <Spinner />;
  }

  const activeProducts =
    productList.find((cat) => cat.name === activeCategory)?.product || [];

  const navigateToCheckout = () => {
    // Clear cart items before navigating to quick order checkout
    dispatch(clearCart());
    navigate(routes.checkout, {
      state: {
        quick: true,
      },
    });
  };

  const showProductDetails = (product: Product) => {
    trackProductSelect({
      id: product.id,
      name: product.name,
      category_id: (product as any).category_id,
      price: Number(product.price) || 0,
      variant: expressWashSelections[product.id] ? 'express' : 'regular',
    });
    setSelectedProduct(product);
    setShowProductModal(true);
  };

  const handleCloseProductModal = () => {
    setShowProductModal(false);
    setSelectedProduct({});
  };

  return (
    <div className="warehouse-products">
      {/* Breadcrumb */}
      {/* <div className="breadcrumb-section mb-3">
        <div className="container">
          <div className="row">
            <div className="col">
              <h1 className="page-title">Shop Product</h1>
              <ul>
                <li>
                  <Link to={routes.home}>Dashboard</Link>
                </li>
                <li className="active">Shop Product</li>
              </ul>
            </div>
          </div>
        </div>
      </div> */}
      <section className="price-section mt-3" id="pricing">
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <div className="quick-order-btn-list">
                <Link
                  to={routes.cart}
                  className="btn btn-primary"
                >
                  View Cart
                </Link>
                <button
                  className="btn btn-primary"
                  onClick={navigateToCheckout}
                >
                  Skip to Quick Order
                </button>
              </div>
            </div>
            <div className="col-md-12">
              {productList.length > 0 ? (
                <>
                  <Swiper
                    spaceBetween={15}
                    navigation={true}
                    modules={[Navigation]}
                    breakpoints={{
                      640: { slidesPerView: 1 },
                      768: { slidesPerView: 4 },
                      1024: { slidesPerView: 6 },
                    }}
                    className="category-swiper"
                  >
                    {productList.map((category, index) => (
                      <SwiperSlide key={`${category.name}-${index}`}>
                        <div
                          className={`category-container ${activeCategory === category.name
                            ? 'active-swiper'
                            : ''
                            }`}
                        >
                          <button
                            type="button"
                            className="category-button-new"
                            onClick={() => handleCategoryClick(category.name)}
                          >
                            <div className="order_list">
                              <div className="prod-img-dv">
                                <img
                                  src={category.image_path}
                                  className="img-fluid"
                                  alt=""
                                />
                              </div>
                              <div className="prod-slider-name">
                                <p>{category.name}</p>
                              </div>
                            </div>
                          </button>
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                  <div className="prodcat-list prodcat-list-card">
                    {activeProducts.length > 0 ? (
                      <>
                        <div className='row g-4'>
                          {activeProducts.map((product) => (
                            <div className='col-xl-3 col-lg-4 col-md-6 col-sm-6 col-12' key={product.id}>
                              <div className="card-prod" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <div className="card-prod-img warehouse-prod-img" style={{ position: 'relative', overflow: 'hidden' }}
                                  onMouseEnter={() => setHoveredProduct(product.id)}
                                  onMouseLeave={() => setHoveredProduct(null)}>
                                  <img
                                    src={product.image_path}
                                    alt={product.name}
                                    loading="lazy"
                                  />
                                  <div className="warehouse-product-overlay" style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'rgba(0, 0, 0, 0.3)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: hoveredProduct === product.id ? 1 : 0,
                                    transition: 'opacity 0.3s ease',
                                    zIndex: 10
                                  }}>
                                    <button
                                      type="button"
                                      className="warehouse-product-view-icon"
                                      style={{
                                        position: 'absolute',
                                        top: '10px',
                                        right: '10px',
                                        background: 'white',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '50%',
                                        width: '36px',
                                        height: '36px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        padding: 0,
                                        fontSize: '16px',
                                        color: '#333',
                                        opacity: 1,
                                        transform: 'scale(0.9)',
                                        zIndex: 11
                                      }}
                                      onClick={() => showProductDetails(product)}
                                    >
                                      <TbEye />
                                    </button>
                                  </div>
                                </div>
                                <div className="card-prod-details" style={{ display: 'flex', flexDirection: 'column' }}>
                                  <div className="d-flex justify-content-between align-items-start mb-2">
                                    <h2 className="mb-0">{product.name}</h2>
                                    {(product as any).combo_details && (product as any).combo_details.length > 0 && (
                                      <span className="badge bg-warning text-dark" style={{ fontSize: '11px', padding: '4px 8px' }}>
                                        COMBO
                                      </span>
                                    )}
                                  </div>
                                  {product.review_count && parseInt(product.review_count) > 0 && product.overall_rating ? (
                                    <div className="product-rating mb-2 d-flex align-items-center">
                                      <div className="stars d-flex align-items-center">
                                        {[...Array(5)].map((_, index) => {
                                          const ratingValue = index + 1;
                                          const hasHalfStar = product.overall_rating! % 1 !== 0;
                                          const isHalfStarPosition = Math.floor(product.overall_rating!) + 1 === ratingValue && hasHalfStar;

                                          if (isHalfStarPosition) {
                                            return (
                                              <FaStarHalfAlt
                                                key={index}
                                                className="me-1 text-warning"
                                                style={{ fontSize: '12px' }}
                                              />
                                            );
                                          } else if (ratingValue <= product.overall_rating!) {
                                            return (
                                              <FaStar
                                                key={index}
                                                className="me-1 text-warning"
                                                style={{ fontSize: '12px' }}
                                              />
                                            );
                                          } else {
                                            return (
                                              <FaStar
                                                key={index}
                                                className="me-1 text-muted"
                                                style={{ fontSize: '12px' }}
                                              />
                                            );
                                          }
                                        })}
                                      </div>
                                      <span className="rating-text ms-2 small text-muted">
                                        {product.overall_rating.toFixed(1)} ({product.review_count} reviews)
                                      </span>
                                    </div>
                                  ) : (
                                    <div className="product-rating mb-2">
                                      <span className="rating-text small text-muted">
                                        No reviews
                                      </span>
                                    </div>
                                  )}

                                  <div className="d-flex justify-content-between align-items-center mb-2" style={{ minHeight: '40px' }}>
                                    <div className="product-price mb-0 d-flex align-items-center">
                                      <div className="amount">
                                        {currencyCode}{' '}
                                        {parseFloat(product.price).toFixed(2)}
                                      </div>
                                    </div>
                                    <div className="input-qty-wrapper d-flex align-items-center">
                                      {cartItems?.some((item) => item.id === (expressWashSelections[product.id] ? `${product.id}_express` : `${product.id}_regular`)) ? (
                                        <div className="input-qty" style={{ height: '36px', marginBottom: 0 }}>
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() =>
                                              handleDecrement(product.id)
                                            }
                                            style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #dee2e6' }}
                                          >
                                            <FiMinus />
                                          </button>
                                          <input
                                            type="number"
                                            className="form-control text-center"
                                            style={{ width: '40px', height: '100%', padding: '0', borderLeft: '0', borderRight: '0', borderRadius: '0' }}
                                            value={quantities[product.id] || 1}
                                            min={1}
                                            max={product.max_qty || 10}
                                            onChange={(e) =>
                                              handleInputChange(
                                                e,
                                                product.id,
                                              )
                                            }
                                          />
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() =>
                                              handleIncrement(
                                                product.id
                                              )
                                            }
                                            style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #dee2e6' }}
                                          >
                                            <FiPlus />
                                          </button>
                                        </div>
                                      ) : (
                                        <button
                                          type="button"
                                          className="button style1"
                                          style={{
                                            borderRadius: '5px',
                                            height: '36px',
                                            minWidth: '80px',
                                            fontSize: '14px',
                                            padding: '0 15px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                          }}
                                          onClick={() => addToCart(product)}
                                        >
                                          Add
                                        </button>
                                      )}
                                    </div>
                                  </div>

                                  {product.express_wash_amount && product.express_wash_amount > 0 && expressWashOnline && (
                                    <div className="express-wash-option mt-2">
                                      <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center">
                                          <span className="express-wash-label small">
                                            Express Wash ({currencyCode}{product.express_wash_amount})
                                          </span>
                                        </div>
                                        <label className="toggle-switch">
                                          <input
                                            type="checkbox"
                                            checked={expressWashSelections[product.id] || false}
                                            onChange={(e) => handleExpressWashToggle(product.id, e.target.checked)}
                                          />
                                          <span className="toggle-slider"></span>
                                        </label>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                <div className="card-prod-btn-wrapper pb-0" style={{ minHeight: '45px', marginTop: '10px', padding: '0 20px 20px' }}>
                                  {cartItems?.some((item) => item.id === (expressWashSelections[product.id] ? `${product.id}_express` : `${product.id}_regular`)) && (
                                    <Link
                                      to={routes.cart}
                                      className="button style1 w-100 d-flex align-items-center justify-content-center text-decoration-none"
                                      style={{ height: '40px', fontSize: '14px' }}
                                    >
                                      <FaShoppingBag className="me-2" />
                                      <span>Go to Cart</span>
                                    </Link>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <NoProducts />
                    )}
                  </div>
                </>
              ) : (
                <NoProducts />
              )}
            </div>
          </div>
        </div>
      </section>
      <ProductView
        isOpen={showProductModal}
        onClose={() => handleCloseProductModal()}
        product={selectedProduct}
      />
    </div>
  );
};

export default WareHouseProduct;
